// Helpers for authorized server media URLs (e.g. `/v0/file/s/<id>?...`).
// The server responds with a 307 redirect to a signed CDN URL and the browser
// follows that redirect automatically for `<img>`/`<video>` sources, so no
// manual HEAD-request resolution is needed.

// Returns true if the given URL points at the server media file endpoint
// (`/v0/file/s/...`) and therefore needs to be resolved to a signed CDN URL.
// Works for both relative URLs and absolute URLs (e.g. `http://localhost:6060/v0/file/s/...`).
// Skips `data:` and `blob:` URLs, and absolute URLs that don't hit the
// server media endpoint (already CDN URLs).
export function needsResolution(url) {
  if (!url || typeof url !== 'string') {
    return false;
  }
  if (url.startsWith('data:') || url.startsWith('blob:')) {
    return false;
  }
  return url.includes('/v0/file/s/');
}

// Converts a relative server URL (e.g. `/v0/file/s/<id>?...`) into an
// absolute URL pointing at the configured Tinode API server. This is needed
// because the webapp may be served from a different origin than the API
// (e.g. webapp on localhost:3001, API on localhost:6060). Without this,
// `<img src>` and `fetch()` both resolve relative URLs against the webapp
// origin, missing the API server entirely.
//
// Absolute URLs, `data:`, and `blob:` URIs are returned unchanged.
export function toAbsoluteServerUrl(tinode, url) {
  if (!url || typeof url !== 'string') {
    return url;
  }
  if (url.startsWith('data:') || url.startsWith('blob:') ||
      url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  if (!tinode || !tinode._host) {
    return url;
  }
  const scheme = tinode._secure ? 'https://' : 'http://';
  const path = url.startsWith('/') ? url : '/' + url;
  return scheme + tinode._host + path;
}

// Returns the authorized URL as-is wrapped in a Promise. The browser follows
// the server's 307 redirect to the signed CDN URL automatically when loading
// `<img>`/`<video>` sources, so no manual resolution is needed. Kept as a
// Promise-returning stub for compatibility with existing callers.
export function resolveSignedUrl(authorizedUrl) {
  return Promise.resolve(authorizedUrl);
}
